// @ts-nocheck
/**
 * カード選出システム
 *
 * 責務:
 * - カードプールからの条件付き選出
 * - コスト範囲フィルタリング
 * - 属性フィルタリング
 * - レアリティフィルタリング
 * - 禁忌カード除外
 */

/**
 * 深度レベル定義（20階刻み）
 * - depth: 深度識別子
 * - maxFloor: この深度の最大階層
 * - costRange: コスト範囲 { min, max }
 * - poolSize: 事前選出するカード数
 */
const DEPTH_LEVELS = [
  { depth: 'outer', maxFloor: 20, costRange: { min: 1, max: 3 }, poolSize: 5 },    // 外縁部
  { depth: 'shallow', maxFloor: 40, costRange: { min: 2, max: 4 }, poolSize: 5 },  // 浅層部
  { depth: 'middle', maxFloor: 60, costRange: { min: 3, max: 6 }, poolSize: 5 },   // 中層部
  { depth: 'deep', maxFloor: 80, costRange: { min: 4, max: 7 }, poolSize: 5 },     // 深部
  { depth: 'abyss', maxFloor: 100, costRange: { min: 5, max: 10 }, poolSize: 5 },  // 最深部
  { depth: 'beyond', maxFloor: Infinity, costRange: { min: 5, max: 10 }, poolSize: 5 }, // 深淵の彼方
];

/**
 * 後方互換性のためのエイリアス
 */
const FLOOR_COST_RANGES = DEPTH_LEVELS.map(level => ({
  maxFloor: level.maxFloor,
  min: level.costRange.min,
  max: level.costRange.max,
}));

/**
 * シード値から擬似乱数を生成
 * @param {number} seed - シード値
 * @returns {function} 0-1の乱数を返す関数
 */
function createSeededRandom(seed) {
  let currentSeed = seed;
  return function() {
    currentSeed = (currentSeed * 1103515245 + 12345) & 0x7fffffff;
    return currentSeed / 0x7fffffff;
  };
}

/**
 * カード選出クラス
 */
class CardSelector {
  /**
   * @param {Object[]} cardPool - 全カードデータ配列
   * @param {number} seed - ダンジョンシード
   */
  constructor(cardPool, seed) {
    this.cardPool = cardPool || [];
    this.seed = seed;
    this.random = createSeededRandom(seed);

    // モンスターカードのみをキャッシュ
    this.monsterCards = this.cardPool.filter(card =>
      card.type === 'monster' && !card.forbidden
    );
  }

  /**
   * フロア階層から深度レベルを取得
   *
   * @param {number} floorNumber - 現在のフロア番号
   * @returns {Object} 深度レベル情報 { depth, maxFloor, costRange, poolSize }
   */
  getDepthLevel(floorNumber) {
    for (const level of DEPTH_LEVELS) {
      if (floorNumber <= level.maxFloor) {
        return level;
      }
    }
    return DEPTH_LEVELS[DEPTH_LEVELS.length - 1];
  }

  /**
   * フロア階層からコスト範囲を取得
   *
   * @param {number} floorNumber - 現在のフロア番号
   * @returns {{ min: number, max: number }} コスト範囲
   */
  getCostRange(floorNumber) {
    const level = this.getDepthLevel(floorNumber);
    return { ...level.costRange };
  }

  /**
   * 条件に合うカードを選出
   *
   * @param {Object} options - 選出オプション
   * @param {string} [options.attribute] - 属性フィルタ（単一）
   * @param {string[]} [options.attributes] - 属性フィルタ（複数、OR条件）
   * @param {{ min: number, max: number }} [options.costRange] - コスト範囲
   * @param {string} [options.rarity] - レアリティフィルタ
   * @param {string} [options.category] - カテゴリフィルタ（部分一致）
   * @param {boolean} [options.excludeForbidden=true] - 禁忌カード除外
   * @param {number} [options.count=1] - 選出数
   * @param {string[]} [options.excludeIds] - 除外するカードID
   * @returns {Object[]} 選出されたカード配列
   */
  selectCards(options = {}) {
    const {
      attribute,
      attributes,
      costRange,
      rarity,
      category,
      excludeForbidden = true,
      count = 1,
      excludeIds = [],
    } = options;

    // フィルタリング
    let candidates = this.monsterCards;

    // 禁忌カード除外（デフォルトで除外）
    if (excludeForbidden) {
      candidates = candidates.filter(card => !card.forbidden);
    }

    // 属性フィルタ
    if (attribute) {
      candidates = candidates.filter(card => card.attribute === attribute);
    } else if (attributes && attributes.length > 0) {
      candidates = candidates.filter(card => attributes.includes(card.attribute));
    }

    // コスト範囲フィルタ
    if (costRange) {
      candidates = candidates.filter(card => {
        const cost = parseInt(card.cost) || 0;
        return cost >= costRange.min && cost <= costRange.max;
      });
    }

    // レアリティは選出時には使わない（敵生成時に付与）
    // カードデータにはレアリティが含まれていないため

    // カテゴリフィルタ
    if (category) {
      candidates = candidates.filter(card =>
        card.category && card.category.includes(category)
      );
    }

    // 除外ID
    if (excludeIds.length > 0) {
      candidates = candidates.filter(card => !excludeIds.includes(card.id));
    }

    // 候補がない場合は空配列
    if (candidates.length === 0) {
      return [];
    }

    // シード値ベースでシャッフル
    const shuffled = this._shuffleWithSeed(candidates);

    // 指定数を返却
    return shuffled.slice(0, count);
  }

  /**
   * 鍵カードの属性から敵属性を決定
   *
   * @param {Object} key1 - 鍵カード1
   * @param {Object} key2 - 鍵カード2
   * @param {string} enemyBias - 大陸のenemyBias属性
   * @returns {string} 選出された属性
   */
  selectEnemyAttribute(key1, key2, enemyBias) {
    const roll = this.random();

    // 70%: 鍵属性のいずれか
    if (roll < 0.7) {
      const keyAttributes = [];
      if (key1?.attribute) keyAttributes.push(key1.attribute);
      if (key2?.attribute && key2.attribute !== key1?.attribute) {
        keyAttributes.push(key2.attribute);
      }
      if (keyAttributes.length > 0) {
        return keyAttributes[Math.floor(this.random() * keyAttributes.length)];
      }
    }

    // 20%: 大陸enemyBias属性
    if (roll < 0.9 && enemyBias) {
      return enemyBias;
    }

    // 10%: ランダム属性
    const allAttributes = ['炎', '水', '光', '闇', '原始', '未来'];
    return allAttributes[Math.floor(this.random() * allAttributes.length)];
  }

  /**
   * 鍵カードからボス候補を取得
   *
   * @param {Object} key1 - 鍵カード1
   * @param {Object} key2 - 鍵カード2
   * @returns {Object|null} ボス候補カード（モンスターの場合）
   */
  getBossFromKeys(key1, key2) {
    const monsterKeys = [];

    if (key1?.type === 'monster') monsterKeys.push(key1);
    if (key2?.type === 'monster') monsterKeys.push(key2);

    if (monsterKeys.length === 0) return null;

    // 両方モンスターならseedで選択
    if (monsterKeys.length === 2) {
      return this.random() < 0.5 ? monsterKeys[0] : monsterKeys[1];
    }

    return monsterKeys[0];
  }

  /**
   * シード値でシャッフル
   * @private
   */
  _shuffleWithSeed(array) {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(this.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  /**
   * 乱数をリセット（同じシードから再生成）
   */
  resetRandom() {
    this.random = createSeededRandom(this.seed);
  }
}

export default CardSelector;
export { CardSelector, DEPTH_LEVELS, FLOOR_COST_RANGES, createSeededRandom };
